import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ReactFlow, Background, Controls, MiniMap, Panel,
  useNodesState, useEdgesState,
  type Node, type Edge, type NodeTypes, Handle, Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Users, Plus, Search, AlertTriangle, Camera, History, X,
  ChevronDown, Building2, User as UserIcon, Briefcase, Zap, GripVertical,
  Trash2, Edit3, Check, ArrowRightLeft, Link as LinkIcon, Network,
  Layout, ChevronLeft, ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  getTalentTree, createEmployee, updateEmployee, deleteEmployee, transferEmployee,
  createTalentUnit, updateTalentUnit, archiveTalentUnit,
  getTalentRisks, createTalentSnapshot, getTalentChanges,
  getEmployeeTags, createEmployeeTag, addEmployeeTag, removeEmployeeTag,
  verifyTalentHubPassword, updateTalentHubPassword,
} from '../api';
import EmployeeBatchImportModal from './EmployeeBatchImportModal';
import {
  User, OrgUnit, Employee, EmployeeTag, TalentRisk, TalentTreeResponse,
  EMPLOYEE_TYPE_LABELS, WORKLOAD_LABELS,
} from '../types';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
}

// ====== Workload colors ======
const WORKLOAD_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  high: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
  medium: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
  low: { bg: 'bg-sky-50', text: 'text-sky-600', dot: 'bg-sky-500' },
};

// ====== Custom Node: Org Unit ======
// Expandable employee list with toggle
function ExpandableEmployeeList({ employees, isShadow, onSelectEmployee, unitId }: { employees: Employee[]; isShadow: boolean; onSelectEmployee?: (emp: Employee) => void; unitId: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayCount = 8;
  const hasMore = employees.length > displayCount;

  const displayEmployees = isExpanded ? employees : employees.slice(0, displayCount);

  // Calculate resignation countdown for each employee
  const now = new Date();
  const sortedEmployees = [...displayEmployees].map(emp => {
    const resignDate = emp.resign_date ? new Date(emp.resign_date) : null;
    const resignDays = resignDate ? Math.ceil((resignDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
    return { emp, resignDays };
  }).sort((a, b) => {
    // Sort by: has resign date first, then by days remaining (soonest first), then by leader
    const aHasResign = a.resignDays !== null;
    const bHasResign = b.resignDays !== null;
    if (aHasResign && !bHasResign) return -1;
    if (!aHasResign && bHasResign) return 1;
    if (aHasResign && bHasResign) return (a.resignDays || 0) - (b.resignDays || 0);
    return (b.emp.is_leader || 0) - (a.emp.is_leader || 0);
  });

  return (
    <div className={clsx('px-2 py-2 space-y-1', !isExpanded && 'max-h-[200px] overflow-y-auto')}>
      {employees.length === 0 && !isShadow && (
        <div className="text-[11px] text-gray-300 text-center py-1">暂无成员</div>
      )}
      {sortedEmployees.map(({ emp, resignDays }) => {
        const wl = WORKLOAD_COLORS[emp.workload] || WORKLOAD_COLORS.medium;
        return (
          <div key={emp.id}
            className={clsx('flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-gray-50 cursor-pointer group transition-colors', emp.is_leader && 'bg-amber-50/50')}
            onClick={(e) => { e.stopPropagation(); onSelectEmployee?.(emp); }}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('employee_id', emp.id);
              e.dataTransfer.setData('source_unit_id', unitId);
              e.dataTransfer.effectAllowed = 'move';
            }}
          >
            <div className={clsx('w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
              emp.is_leader ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' :
              emp.employee_type === 'intern' ? 'bg-violet-100 text-violet-600' : 'bg-primary-100 text-primary-600'
            )}>
              {emp.avatar_url ? <img src={emp.avatar_url} className="w-6 h-6 rounded-full object-cover" alt="" /> : emp.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-gray-700 truncate">{emp.name}</span>
                {resignDays !== null && (
                  <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-600 font-medium">剩{resignDays}天</span>
                )}
                {emp.title && <span className="text-[10px] text-gray-400 truncate">· {emp.title}</span>}
              </div>
              {emp.core_output && (
                <div className="text-[10px] text-gray-400 truncate">{emp.core_output}</div>
              )}
            </div>
            <span className={clsx('w-2 h-2 rounded-full shrink-0', wl.dot)} title={WORKLOAD_LABELS[emp.workload]} />
          </div>
        );
      })}
      {hasMore && (
        <button
          onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          className="w-full py-1.5 text-[11px] text-primary-600 hover:bg-primary-50 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          {isExpanded ? (
            <>收起 <ChevronDown size={12} className="rotate-180" /></>
          ) : (
            <>展开 {employees.length - displayCount} 人 <ChevronDown size={12} /></>
          )}
        </button>
      )}
    </div>
  );
}

// Inline add member — XMind-like: click to show input, type name + Enter
function InlineAddMember({ unitId, onAdd, onFullAdd }: { unitId: string; onAdd?: (unitId: string, name: string) => void; onFullAdd?: () => void }) {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const handleSubmit = () => {
    if (name.trim()) {
      onAdd?.(unitId, name.trim());
      setName('');
      // Stay in editing mode for rapid adding
    } else {
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <div className="px-2 pb-1 flex items-center gap-1">
        <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className="flex-1 flex items-center justify-center gap-1 py-1 text-[11px] text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg border border-dashed border-gray-200 hover:border-primary-300 transition-colors">
          <Plus size={11} />快速添加
        </button>
        <button onClick={(e) => { e.stopPropagation(); onFullAdd?.(); }}
          className="p-1 text-gray-300 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors" title="详细添加">
          <Edit3 size={10} />
        </button>
      </div>
    );
  }

  return (
    <div className="px-2 pb-1" onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') { setName(''); setEditing(false); }
        }}
        onBlur={handleSubmit}
        placeholder="输入姓名，回车添加"
        className="w-full px-2 py-1.5 text-[11px] border border-primary-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-primary-50/50"
      />
    </div>
  );
}

// Inline add child node — Tab-like in XMind
function InlineAddChildNode({ parentId, onAdd }: { parentId: string; onAdd?: (parentId: string, name: string) => void }) {
  const [editing, setEditing] = React.useState(false);
  const [name, setName] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const handleSubmit = () => {
    if (name.trim()) {
      onAdd?.(parentId, name.trim());
      setName('');
      setEditing(false);
    } else {
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="w-full flex items-center justify-center gap-1 py-1 text-[10px] text-gray-300 hover:text-violet-500 hover:bg-violet-50 rounded-lg border border-dashed border-gray-100 hover:border-violet-300 transition-colors">
        <Plus size={9} />子节点
      </button>
    );
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') { setName(''); setEditing(false); }
        }}
        onBlur={handleSubmit}
        placeholder="子节点名称，回车创建"
        className="w-full px-2 py-1.5 text-[11px] border border-violet-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500 bg-violet-50/50"
      />
    </div>
  );
}

function OrgUnitNode({ data }: { data: any }) {
  const unit: OrgUnit = data.unit;
  const stats = data.stats || {};
  const employees: Employee[] = data.employees || [];
  const isShadow = !!unit.is_shadow;
  const hcGap = (unit.hc_plan || 0) - (stats.employee_count || 0);
  const riskColor = hcGap > (unit.hc_plan || 1) * 0.5 ? 'border-red-400' : hcGap > 0 ? 'border-amber-400' : 'border-emerald-300';

  return (
    <div className={clsx(
      'rounded-xl shadow-sm border-2 bg-white min-w-[220px] max-w-[280px] transition-all select-none',
      isShadow ? 'border-dashed border-gray-300 opacity-70' : riskColor
    )}>
      <Handle type="target" position={Position.Top} className="!w-3 !h-1.5 !bg-gray-300 !border-0 !rounded-full !-top-1" />

      {/* Header */}
      <div className={clsx('px-3 py-2 rounded-t-[10px] flex items-center gap-2', isShadow ? 'bg-gray-50' : 'bg-gradient-to-r from-primary-50 to-primary-100/50')}>
        <Building2 size={14} className={isShadow ? 'text-gray-400' : 'text-primary-500'} />
        <span className={clsx('text-sm font-semibold flex-1 truncate', isShadow ? 'text-gray-400' : 'text-gray-800')}>
          {unit.name}
        </span>
        {isShadow && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">待招聘</span>}
        <button onClick={(e) => { e.stopPropagation(); data.onEdit?.(unit); }}
          className="p-0.5 rounded hover:bg-white/60 text-gray-400 hover:text-gray-600 transition-colors">
          <Edit3 size={12} />
        </button>
      </div>

      {/* Stats bar — compact single row with leader inline */}
      <div className="px-3 py-1.5 flex items-center gap-2 text-[11px] text-gray-500 border-b border-gray-100 flex-wrap">
        {(() => {
          const leader = employees.find(e => e.is_leader);
          return leader ? (
            <span className="flex items-center gap-1 text-amber-600 font-medium">
              <span className="text-[9px] px-1 py-0 rounded bg-amber-100">Leader</span>
              {leader.name}
            </span>
          ) : null;
        })()}
        <span className="flex items-center gap-1">
          <Users size={11} />{stats.employee_count || 0}{unit.hc_plan ? `/${unit.hc_plan}` : ''}人
        </span>
        {stats.total_monthly_cost > 0 && (
          <span className="text-amber-600">¥{(stats.total_monthly_cost).toFixed(0)}K/月</span>
        )}
        {hcGap > 0 && <span className="text-red-500 font-medium">缺{hcGap}人</span>}
      </div>

      {/* Description */}
      {unit.description && (
        <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
          <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-3">{unit.description}</p>
        </div>
      )}

      {/* Employee chips with expand/collapse */}
      <ExpandableEmployeeList
        employees={employees}
        isShadow={isShadow}
        onSelectEmployee={data.onSelectEmployee}
        unitId={unit.id}
      />

      {/* Inline add member (XMind-like) */}
      {data.canEdit && (
        <InlineAddMember unitId={unit.id} onAdd={data.onInlineAdd} onFullAdd={() => data.onAddEmployee?.(unit.id)} />
      )}

      {/* Add child node button */}
      {data.canEdit && (
        <div className="px-2 pb-2">
          <InlineAddChildNode parentId={unit.id} onAdd={data.onInlineAddUnit} />
        </div>
      )}

      <Handle type="source" position={Position.Bottom} className="!w-3 !h-1.5 !bg-gray-300 !border-0 !rounded-full !-bottom-1" />
    </div>
  );
}

// ====== Layout helpers ======
const NODE_WIDTH = 260;
const NODE_GAP_X = 80;
const NODE_GAP_Y_MIN = 60; // minimum vertical gap between parent bottom and child top

// Estimate node height based on employee count and content
function estimateNodeHeight(unit: OrgUnit, empCount: number): number {
  let h = 0;
  h += 36;  // header
  h += 28;  // stats bar
  if (unit.description) h += 40;  // description
  const displayCount = Math.min(empCount, 8); // collapsed shows max 8
  h += displayCount * 32;  // employee rows
  if (empCount > 8) h += 28; // expand button
  h += 36;  // add member button + padding
  return Math.max(h, 100);
}

function buildTreeLayout(units: OrgUnit[], employees: Employee[], unitStats: Record<string, any>, callbacks: any) {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Build parent->children map
  const childrenMap: Record<string, OrgUnit[]> = {};
  const roots: OrgUnit[] = [];
  for (const u of units) {
    if (u.parent_id && units.some(p => p.id === u.parent_id)) {
      if (!childrenMap[u.parent_id]) childrenMap[u.parent_id] = [];
      childrenMap[u.parent_id].push(u);
    } else {
      roots.push(u);
    }
  }

  // Pre-compute employee counts per unit (direct only, for height estimation)
  const empCountMap: Record<string, number> = {};
  for (const u of units) {
    empCountMap[u.id] = employees.filter(e => e.org_unit_id === u.id).length;
  }

  // Pre-compute estimated heights
  const heightMap: Record<string, number> = {};
  for (const u of units) {
    heightMap[u.id] = estimateNodeHeight(u, empCountMap[u.id]);
  }

  // Assign depth (level) to each unit
  const depthMap: Record<string, number> = {};
  function assignDepth(unitId: string, depth: number) {
    depthMap[unitId] = depth;
    for (const child of (childrenMap[unitId] || [])) {
      assignDepth(child.id, depth + 1);
    }
  }
  for (const root of roots) assignDepth(root.id, 0);

  // Compute max height per level
  const maxHeightPerLevel: Record<number, number> = {};
  for (const u of units) {
    const d = depthMap[u.id];
    maxHeightPerLevel[d] = Math.max(maxHeightPerLevel[d] || 0, heightMap[u.id]);
  }

  // Compute cumulative Y offset per level (each level starts after the tallest node of previous level + gap)
  const levelY: Record<number, number> = {};
  const maxDepth = Math.max(...Object.values(depthMap), 0);
  levelY[0] = 0;
  for (let d = 1; d <= maxDepth; d++) {
    levelY[d] = levelY[d - 1] + (maxHeightPerLevel[d - 1] || 100) + NODE_GAP_Y_MIN;
  }

  // Compute subtree widths
  const widthCache: Record<string, number> = {};
  function getSubtreeWidth(unitId: string): number {
    if (widthCache[unitId] !== undefined) return widthCache[unitId];
    const children = childrenMap[unitId] || [];
    if (children.length === 0) {
      widthCache[unitId] = NODE_WIDTH;
      return NODE_WIDTH;
    }
    const total = children.reduce((sum, c) => sum + getSubtreeWidth(c.id) + NODE_GAP_X, -NODE_GAP_X);
    widthCache[unitId] = Math.max(total, NODE_WIDTH);
    return widthCache[unitId];
  }

  // Place nodes recursively
  function placeNode(unit: OrgUnit, x: number) {
    const unitEmps = employees.filter(e => e.org_unit_id === unit.id);
    const y = levelY[depthMap[unit.id]] || 0;
    nodes.push({
      id: unit.id,
      type: 'orgUnit',
      position: { x, y },
      data: {
        unit,
        stats: unitStats[unit.id] || {},
        employees: unitEmps,
        ...callbacks,
      },
    });

    const children = childrenMap[unit.id] || [];
    if (children.length > 0) {
      const totalWidth = children.reduce((sum, c) => sum + getSubtreeWidth(c.id) + NODE_GAP_X, -NODE_GAP_X);
      let cx = x + NODE_WIDTH / 2 - totalWidth / 2;
      for (const child of children) {
        const cw = getSubtreeWidth(child.id);
        placeNode(child, cx + cw / 2 - NODE_WIDTH / 2);
        edges.push({
          id: `${unit.id}-${child.id}`,
          source: unit.id,
          target: child.id,
          type: 'smoothstep',
          style: { stroke: child.is_shadow ? '#d1d5db' : '#c7d2fe', strokeWidth: 2, strokeDasharray: child.is_shadow ? '5 5' : undefined },
        });
        cx += cw + NODE_GAP_X;
      }
    }
  }

  // Place all roots
  const totalRootWidth = roots.reduce((sum, r) => sum + getSubtreeWidth(r.id) + NODE_GAP_X, -NODE_GAP_X);
  let rx = -totalRootWidth / 2;
  for (const root of roots) {
    const rw = getSubtreeWidth(root.id);
    placeNode(root, rx + rw / 2 - NODE_WIDTH / 2);
    rx += rw + NODE_GAP_X;
  }

  return { nodes, edges };
}

// ====== Main Component ======
export default function TalentHub({ currentUser, notificationBell }: Props) {
  const [treeData, setTreeData] = useState<TalentTreeResponse | null>(null);
  const [risks, setRisks] = useState<TalentRisk[]>([]);
  const [changes, setChanges] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showAddEmployee, setShowAddEmployee] = useState<string | null>(null); // org_unit_id
  const [showBatchImport, setShowBatchImport] = useState(false);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [editingUnit, setEditingUnit] = useState<OrgUnit | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showRisks, setShowRisks] = useState(false);
  const [showChanges, setShowChanges] = useState(false);

  // Password protection - ALL users need password (stored in backend database)
  const TALENT_HUB_AUTHORIZED_KEY = 'talent_hub_authorized';

  const [isAuthorized, setIsAuthorized] = useState(() => {
    return localStorage.getItem(TALENT_HUB_AUTHORIZED_KEY) === 'true';
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  // Forms
  const [empForm, setEmpForm] = useState({ name: '', employee_type: 'full_time', title: '', level: '', core_output: '', workload: 'medium', monthly_cost: 0, hire_date: '', is_leader: false, resign_date: '' });
  const [unitForm, setUnitForm] = useState({ name: '', type: 'team', parent_id: '', hc_plan: 0, description: '', is_shadow: false });

  const canEdit = ['admin', 'hr', 'vip'].includes(currentUser.role);

  const nodeTypes: NodeTypes = useMemo(() => ({ orgUnit: OrgUnitNode }), []);

  const fetchData = useCallback(async () => {
    try {
      const [tree, riskData] = await Promise.all([getTalentTree(), getTalentRisks()]);
      setTreeData(tree);
      setRisks(riskData);
    } catch (err) {
      toast.error('加载组织数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Inline add employee (quick - just name)
  const handleInlineAddEmployee = useCallback(async (unitId: string, name: string) => {
    try {
      await createEmployee({ name, org_unit_id: unitId, employee_type: 'full_time', workload: 'medium' });
      toast.success(`已添加「${name}」`);
      fetchData();
    } catch { toast.error('添加失败'); }
  }, [fetchData]);

  // Inline add child unit (quick - just name)
  const handleInlineAddUnit = useCallback(async (parentId: string, name: string) => {
    try {
      await createTalentUnit({ name, type: 'team', parent_id: parentId, hc_plan: 0, description: '', is_shadow: false });
      toast.success(`已创建「${name}」`);
      fetchData();
    } catch { toast.error('创建失败'); }
  }, [fetchData]);

  // Build React Flow layout when data changes
  useEffect(() => {
    if (!treeData) return;
    const callbacks = {
      canEdit,
      onEdit: (unit: OrgUnit) => setEditingUnit(unit),
      onAddEmployee: (unitId: string) => {
        setShowAddEmployee(unitId);
        setEmpForm({ name: '', employee_type: 'full_time', title: '', level: '', core_output: '', workload: 'medium', monthly_cost: 0, hire_date: '', is_leader: false, resign_date: '' });
      },
      onSelectEmployee: (emp: Employee) => setSelectedEmployee(emp),
      onInlineAdd: handleInlineAddEmployee,
      onInlineAddUnit: handleInlineAddUnit,
    };
    const { nodes: n, edges: e } = buildTreeLayout(treeData.units, treeData.employees, treeData.unitStats, callbacks);
    setNodes(n);
    setEdges(e);
  }, [treeData, canEdit, setNodes, setEdges, handleInlineAddEmployee, handleInlineAddUnit]);

  // Handle drop employee onto different unit node
  const onDrop = useCallback(async (event: React.DragEvent) => {
    event.preventDefault();
    const empId = event.dataTransfer.getData('employee_id');
    if (!empId) return;

    // Find the node the drop landed on
    const target = (event.target as HTMLElement).closest('[data-id]');
    const targetNodeId = target?.getAttribute('data-id');
    if (!targetNodeId || !treeData) return;

    const emp = treeData.employees.find(e => e.id === empId);
    if (!emp || emp.org_unit_id === targetNodeId) return;

    const targetUnit = treeData.units.find(u => u.id === targetNodeId);
    if (!window.confirm(`确认将「${emp.name}」调动到「${targetUnit?.name || '目标部门'}」？`)) return;

    try {
      await transferEmployee(empId, targetNodeId);
      toast.success('调动成功');
      fetchData();
    } catch {
      toast.error('调动失败');
    }
  }, [treeData, fetchData]);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Search highlight
  const filteredEmployees = useMemo(() => {
    if (!searchQuery || !treeData) return null;
    const q = searchQuery.toLowerCase();
    return treeData.employees.filter(e =>
      e.name.toLowerCase().includes(q) ||
      e.title.toLowerCase().includes(q) ||
      e.core_output.toLowerCase().includes(q) ||
      e.tags?.some(t => t.name.toLowerCase().includes(q))
    );
  }, [searchQuery, treeData]);

  const handleCreateEmployee = async () => {
    if (!empForm.name.trim()) { toast.error('请输入姓名'); return; }
    try {
      await createEmployee({ ...empForm, org_unit_id: showAddEmployee });
      toast.success('员工已添加');
      setShowAddEmployee(null);
      fetchData();
    } catch { toast.error('添加失败'); }
  };

  const handleCreateUnit = async () => {
    if (!unitForm.name.trim()) { toast.error('请输入名称'); return; }
    try {
      await createTalentUnit(unitForm);
      toast.success('组织节点已创建');
      setShowAddUnit(false);
      setUnitForm({ name: '', type: 'team', parent_id: '', hc_plan: 0, description: '', is_shadow: false });
      fetchData();
    } catch { toast.error('创建失败'); }
  };

  const handleUpdateUnit = async () => {
    if (!editingUnit) return;
    try {
      await updateTalentUnit(editingUnit.id, editingUnit);
      toast.success('已更新');
      setEditingUnit(null);
      fetchData();
    } catch { toast.error('更新失败'); }
  };

  const handleUpdateEmployee = async () => {
    if (!selectedEmployee) return;
    try {
      await updateEmployee(selectedEmployee.id, selectedEmployee);
      toast.success('已更新');
      setSelectedEmployee(null);
      fetchData();
    } catch { toast.error('更新失败'); }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    if (!window.confirm(`确认删除员工「${selectedEmployee.name}」？`)) return;
    try {
      await deleteEmployee(selectedEmployee.id);
      toast.success('已删除');
      setSelectedEmployee(null);
      fetchData();
    } catch { toast.error('删除失败'); }
  };

  // Auto layout: re-calculate and apply tree layout
  const handleAutoLayout = useCallback(() => {
    if (!treeData || !treeData.units.length) return;

    const callbacks = {
      onEdit: setEditingUnit,
      onAddEmployee: setShowAddEmployee,
      onSelectEmployee: setSelectedEmployee,
      onInlineAdd: handleInlineAddEmployee,
      onInlineAddUnit: handleInlineAddUnit,
      canEdit,
    };

    // Build new layout with current tree data
    const { nodes: newNodes, edges: newEdges } = buildTreeLayout(
      treeData.units,
      treeData.employees,
      treeData.unitStats,
      callbacks
    );

    // Apply new positions with animation
    setNodes((prevNodes) => {
      const nodeMap = new Map(prevNodes.map(n => [n.id, n]));

      return newNodes.map(newNode => {
        const existing = nodeMap.get(newNode.id);
        return {
          ...newNode,
          data: existing?.data || newNode.data,
          // Keep any user-modified styling
          style: existing?.style,
        };
      });
    });

    setEdges(newEdges);
    toast.success('布局已整理');
  }, [treeData, canEdit, setNodes, setEdges, setEditingUnit, setShowAddEmployee, setSelectedEmployee, handleInlineAddEmployee, handleInlineAddUnit]);

  const handleSnapshot = async () => {
    const name = window.prompt('快照名称', `组织架构 ${new Date().toLocaleDateString('zh-CN')}`);
    if (!name) return;
    try {
      await createTalentSnapshot(name);
      toast.success('快照已保存');
    } catch { toast.error('保存失败'); }
  };

  const criticalCount = risks.filter(r => r.type === 'critical').length;
  const warningCount = risks.filter(r => r.type === 'warning').length;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  // Password gate - ALL users need password
  if (!isAuthorized) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-sm">
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <Network size={24} className="text-primary-600" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 text-center mb-2">人才中枢</h2>
          <p className="text-sm text-gray-500 text-center mb-6">此页面包含敏感信息，请输入密码访问</p>

          {!showChangePassword ? (
            <div className="space-y-3">
              <input
                type="password"
                value={passwordInput}
                onChange={e => { setPasswordInput(e.target.value); setPasswordError(false); }}
                placeholder="请输入密码"
                className={clsx('w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500',
                  passwordError ? 'border-red-300 bg-red-50' : 'border-gray-200')}
              />
              {passwordError && <p className="text-xs text-red-500">密码错误，请重试</p>}
              <button
                onClick={async () => {
                  setIsVerifying(true);
                  try {
                    const result = await verifyTalentHubPassword(passwordInput);
                    if (result.valid) {
                      localStorage.setItem(TALENT_HUB_AUTHORIZED_KEY, 'true');
                      setIsAuthorized(true);
                    } else {
                      setPasswordError(true);
                    }
                  } catch {
                    setPasswordError(true);
                  } finally {
                    setIsVerifying(false);
                  }
                }}
                disabled={isVerifying}
                className="w-full py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isVerifying ? '验证中...' : '进入'}
              </button>
              {/* Admin can change password */}
              {currentUser.role === 'admin' && (
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="w-full py-2 text-xs text-gray-500 hover:text-primary-600 transition-colors"
                >
                  修改密码
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">修改密码</p>
              <input
                type="password"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setChangePasswordError(''); setChangePasswordSuccess(false); }}
                placeholder="新密码"
                className="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setChangePasswordError(''); setChangePasswordSuccess(false); }}
                placeholder="确认新密码"
                className="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {changePasswordError && <p className="text-xs text-red-500">{changePasswordError}</p>}
              {changePasswordSuccess && <p className="text-xs text-emerald-600">密码修改成功！</p>}
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (newPassword.length < 6) {
                      setChangePasswordError('密码至少需要6位');
                      return;
                    }
                    if (newPassword !== confirmPassword) {
                      setChangePasswordError('两次输入的密码不一致');
                      return;
                    }
                    setIsChangingPassword(true);
                    try {
                      await updateTalentHubPassword(newPassword);
                      setChangePasswordSuccess(true);
                      setTimeout(() => {
                        setShowChangePassword(false);
                        setNewPassword('');
                        setConfirmPassword('');
                        setChangePasswordSuccess(false);
                      }, 1500);
                    } catch {
                      setChangePasswordError('密码修改失败');
                    } finally {
                      setIsChangingPassword(false);
                    }
                  }}
                  disabled={isChangingPassword}
                  className="flex-1 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isChangingPassword ? '修改中...' : '确认修改'}
                </button>
                <button
                  onClick={() => {
                    setShowChangePassword(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setChangePasswordError('');
                    setChangePasswordSuccess(false);
                  }}
                  className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 rounded-lg transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-400 text-center mt-4">仅限 HR 团队和老板访问</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
            <Users size={16} className="text-violet-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800">人才中枢</h1>
            <p className="text-sm text-gray-400">
              {treeData?.units.length || 0}个节点 · {treeData?.employees.length || 0}名员工
            </p>
          </div>
        </div>

        {/* Search — sticky wrapper */}
        {!isHeaderCollapsed && (
          <div className="flex-1 max-w-xs relative sticky top-0 z-10">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="搜索姓名、技能标签..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Risk badges */}
        {!isHeaderCollapsed && (
          <button onClick={() => setShowRisks(!showRisks)}
            className={clsx('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              criticalCount > 0 ? 'bg-red-50 text-red-600 border border-red-200' :
              warningCount > 0 ? 'bg-amber-50 text-amber-600 border border-amber-200' :
              'bg-emerald-50 text-emerald-600 border border-emerald-200')}>
            <AlertTriangle size={13} />
            {criticalCount > 0 ? `${criticalCount}个用人缺口` : warningCount > 0 ? `${warningCount}个警告` : '组织健康'}
          </button>
        )}

        {/* Collapse button */}
        <button onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          title={isHeaderCollapsed ? '展开' : '收缩'}>
          {isHeaderCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          {isHeaderCollapsed ? '展开' : '收缩'}
        </button>

        {/* Actions */}
        {!isHeaderCollapsed && canEdit && (
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowAddUnit(true); setUnitForm({ name: '', type: 'team', parent_id: '', hc_plan: 0, description: '', is_shadow: false }); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
              <Plus size={13} />添加节点
            </button>
            <button onClick={() => setShowBatchImport(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              <Plus size={13} />批量导入
            </button>
            <button onClick={handleSnapshot}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              <Camera size={13} />快照
            </button>
            <button onClick={async () => { setShowChanges(!showChanges); if (!showChanges) { try { setChanges(await getTalentChanges()); } catch {} } }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              <History size={13} />变更
            </button>
            {/* Admin change password */}
            {currentUser.role === 'admin' && (
              <button onClick={() => setShowChangePassword(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                <Edit3 size={13} />改密码
              </button>
            )}
          </div>
        )}
        {/* Auto layout button - available for all authorized users */}
        <button onClick={handleAutoLayout}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors"
          title="一键整理布局">
          <Layout size={13} />整理
        </button>
        {notificationBell}
      </div>

      {/* Search results overlay */}
      {filteredEmployees && filteredEmployees.length > 0 && (
        <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">找到 {filteredEmployees.length} 人：</span>
            {filteredEmployees.slice(0, 10).map(emp => (
              <button key={emp.id} onClick={() => setSelectedEmployee(emp)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100">
                <UserIcon size={11} />{emp.name}
                <span className="text-gray-400">({treeData?.units.find(u => u.id === emp.org_unit_id)?.name || '未分配'})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Risk panel */}
      {showRisks && risks.length > 0 && (
        <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-3 max-h-40 overflow-y-auto">
          <div className="space-y-1.5">
            {risks.map((r, i) => (
              <div key={i} className={clsx('flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs',
                r.type === 'critical' ? 'bg-red-50 text-red-700' : r.type === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-600')}>
                <AlertTriangle size={12} />
                <span className="font-medium">{r.unit_name}</span>
                <span>{r.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Changes panel */}
      {showChanges && (
        <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-3 max-h-48 overflow-y-auto">
          <div className="space-y-1.5">
            {changes.length === 0 && <div className="text-xs text-gray-400">暂无变更记录</div>}
            {changes.slice(0, 15).map(c => (
              <div key={c.id} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="text-gray-400 shrink-0">{c.changed_at?.slice(5, 16)}</span>
                <span className="font-medium">{c.changed_by_name}</span>
                <span>{c.summary}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* React Flow Canvas */}
      <div className="flex-1" onDrop={onDrop} onDragOver={onDragOver}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={true}
        >
          <Background color="#e5e7eb" gap={24} size={1} />
          <Controls position="bottom-right" showInteractive={false} />
          <MiniMap
            position="bottom-left"
            nodeStrokeWidth={3}
            nodeColor={(node) => {
              const unit = (node.data as any)?.unit;
              if (unit?.is_shadow) return '#d1d5db';
              const stats = (node.data as any)?.stats;
              const hcGap = (unit?.hc_plan || 0) - (stats?.employee_count || 0);
              if (hcGap > (unit?.hc_plan || 1) * 0.5) return '#f87171';
              if (hcGap > 0) return '#fbbf24';
              return '#6ee7b7';
            }}
            style={{ borderRadius: 8 }}
          />

          {/* Empty state */}
          {nodes.length === 0 && (
            <Panel position="top-center">
              <div className="mt-32 text-center">
                <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-500 mb-2">还没有组织架构</h3>
                <p className="text-sm text-gray-400 mb-4">点击"添加节点"开始搭建你的组织树</p>
                {canEdit && (
                  <button onClick={() => setShowAddUnit(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg">
                    <Plus size={14} className="inline mr-1" />创建第一个节点
                  </button>
                )}
              </div>
            </Panel>
          )}
        </ReactFlow>
      </div>

      {/* ====== Modal: Add Employee ====== */}
      {showAddEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddEmployee(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">添加员工</h3>
            <div className="space-y-3">
              <input value={empForm.name} onChange={e => setEmpForm(f => ({ ...f, name: e.target.value }))}
                placeholder="姓名 *" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              <div className="grid grid-cols-2 gap-3">
                <select value={empForm.employee_type} onChange={e => setEmpForm(f => ({ ...f, employee_type: e.target.value }))}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                  <option value="full_time">正式员工</option>
                  <option value="intern">实习生</option>
                  <option value="outsource">外包</option>
                  <option value="consultant">顾问</option>
                </select>
                <select value={empForm.workload} onChange={e => setEmpForm(f => ({ ...f, workload: e.target.value }))}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                  <option value="low">低负荷</option>
                  <option value="medium">正常</option>
                  <option value="high">高负荷</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={empForm.title} onChange={e => setEmpForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="职位" className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                <input value={empForm.level} onChange={e => setEmpForm(f => ({ ...f, level: e.target.value }))}
                  placeholder="职级 (如P5)" className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <input value={empForm.core_output} onChange={e => setEmpForm(f => ({ ...f, core_output: e.target.value.slice(0, 20) }))}
                placeholder="核心职责 (限20字)" maxLength={20} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" step="1" value={empForm.monthly_cost || ''} onChange={e => setEmpForm(f => ({ ...f, monthly_cost: parseFloat(e.target.value) || 0 }))}
                  placeholder="月薪成本 (K)" className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                <input type="date" value={empForm.hire_date} onChange={e => setEmpForm(f => ({ ...f, hire_date: e.target.value }))}
                  placeholder="入职日期" className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600">
                  <input type="checkbox" checked={empForm.is_leader} onChange={e => setEmpForm(f => ({ ...f, is_leader: e.target.checked }))}
                    className="rounded border-gray-300" />
                  设为组长/Leader
                </label>
                <input type="date" value={empForm.resign_date} onChange={e => setEmpForm(f => ({ ...f, resign_date: e.target.value }))}
                  placeholder="预计离职日期" title="预计离职日期" className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAddEmployee(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
              <button onClick={handleCreateEmployee} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg">添加</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Modal: Batch Import Employees ====== */}
      {showBatchImport && (
        <EmployeeBatchImportModal onClose={() => setShowBatchImport(false)} onDone={fetchData} />
      )}

      {/* ====== Modal: Add Unit ====== */}
      {showAddUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddUnit(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">创建组织节点</h3>
            <div className="space-y-3">
              <input value={unitForm.name} onChange={e => setUnitForm(f => ({ ...f, name: e.target.value }))}
                placeholder="节点名称 *" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              <div className="grid grid-cols-2 gap-3">
                <select value={unitForm.type} onChange={e => setUnitForm(f => ({ ...f, type: e.target.value }))}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                  <option value="department">部门</option>
                  <option value="team">团队</option>
                  <option value="squad">小组</option>
                </select>
                <select value={unitForm.parent_id} onChange={e => setUnitForm(f => ({ ...f, parent_id: e.target.value }))}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                  <option value="">顶级节点</option>
                  {treeData?.units.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" value={unitForm.hc_plan || ''} onChange={e => setUnitForm(f => ({ ...f, hc_plan: parseInt(e.target.value) || 0 }))}
                  placeholder="编制人数" className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600">
                  <input type="checkbox" checked={unitForm.is_shadow} onChange={e => setUnitForm(f => ({ ...f, is_shadow: e.target.checked }))}
                    className="rounded border-gray-300" />
                  影子岗位 <span className="text-gray-400">(虚位以待，待招聘)</span>
                </label>
              </div>
              <textarea value={unitForm.description} onChange={e => setUnitForm(f => ({ ...f, description: e.target.value }))}
                placeholder="团队职责描述..." rows={4} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAddUnit(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
              <button onClick={handleCreateUnit} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg">创建</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== Modal: Edit Unit ====== */}
      {editingUnit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditingUnit(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">编辑节点: {editingUnit.name}</h3>
            <div className="space-y-3">
              <input value={editingUnit.name} onChange={e => setEditingUnit({ ...editingUnit, name: e.target.value })}
                placeholder="名称" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              <div className="grid grid-cols-2 gap-3">
                <select value={editingUnit.type} onChange={e => setEditingUnit({ ...editingUnit, type: e.target.value as any })}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
                  <option value="department">部门</option>
                  <option value="team">团队</option>
                  <option value="squad">小组</option>
                </select>
                <input type="number" value={editingUnit.hc_plan || ''} onChange={e => setEditingUnit({ ...editingUnit, hc_plan: parseInt(e.target.value) || 0 })}
                  placeholder="编制人数" className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <label className="block">
                <span className="text-xs text-gray-500">上级节点</span>
                <select value={editingUnit.parent_id || ''} onChange={e => setEditingUnit({ ...editingUnit, parent_id: e.target.value || null })}
                  className="w-full mt-0.5 px-3 py-2 text-sm border border-gray-200 rounded-lg">
                  <option value="">顶级节点 (无上级)</option>
                  {treeData?.units.filter(u => u.id !== editingUnit.id).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </label>
              <textarea value={editingUnit.description} onChange={e => setEditingUnit({ ...editingUnit, description: e.target.value })}
                placeholder="团队职责描述..." rows={4} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
            </div>
            <div className="flex justify-between mt-4">
              <button onClick={async () => {
                if (!window.confirm(`确认归档「${editingUnit.name}」？`)) return;
                try { await archiveTalentUnit(editingUnit.id); toast.success('已归档'); setEditingUnit(null); fetchData(); } catch { toast.error('归档失败'); }
              }} className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">归档</button>
              <div className="flex gap-2">
                <button onClick={() => setEditingUnit(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
                <button onClick={handleUpdateUnit} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====== Drawer: Employee Detail ====== */}
      {selectedEmployee && (
        <div className="fixed inset-y-0 right-0 z-50 flex">
          <div className="absolute inset-0" onClick={() => setSelectedEmployee(null)} />
          <div className="relative ml-auto w-80 bg-white shadow-xl border-l border-gray-200 flex flex-col h-full overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">员工详情</h3>
              <button onClick={() => setSelectedEmployee(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Avatar + Name */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-lg font-bold text-primary-600">
                  {selectedEmployee.avatar_url ? <img src={selectedEmployee.avatar_url} className="w-12 h-12 rounded-full object-cover" alt="" /> : selectedEmployee.name.charAt(0)}
                </div>
                <div>
                  <input value={selectedEmployee.name} onChange={e => setSelectedEmployee({ ...selectedEmployee, name: e.target.value })}
                    className="text-base font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none w-full" />
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium',
                      selectedEmployee.employee_type === 'intern' ? 'bg-violet-100 text-violet-600' : 'bg-primary-50 text-primary-600')}>
                      {EMPLOYEE_TYPE_LABELS[selectedEmployee.employee_type]}
                    </span>
                    {selectedEmployee.level && <span className="text-[10px] text-gray-400">{selectedEmployee.level}</span>}
                  </div>
                </div>
              </div>

              {/* Time info */}
              {selectedEmployee.hire_date && (() => {
                const hireDate = new Date(selectedEmployee.hire_date);
                const now = new Date();
                const diffMs = now.getTime() - hireDate.getTime();
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                const months = Math.floor(diffDays / 30);
                const years = Math.floor(months / 12);
                const tenure = years > 0 ? `${years}年${months % 12}个月` : months > 0 ? `${months}个月` : `${diffDays}天`;
                const resignDate = selectedEmployee.resign_date ? new Date(selectedEmployee.resign_date) : null;
                const resignDays = resignDate ? Math.ceil((resignDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">入职 {selectedEmployee.hire_date}</span>
                    <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-600">在职 {tenure}</span>
                    {resignDays !== null && resignDays > 0 && (
                      <span className="px-2 py-0.5 rounded bg-red-50 text-red-600">离职倒计时 {resignDays}天</span>
                    )}
                    {resignDays !== null && resignDays <= 0 && (
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-700">已到期/已离职</span>
                    )}
                  </div>
                );
              })()}

              {/* Fields */}
              <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-xs text-gray-500">职位</span>
                    <input value={selectedEmployee.title} onChange={e => setSelectedEmployee({ ...selectedEmployee, title: e.target.value })}
                      className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-500">职级</span>
                    <input value={selectedEmployee.level} onChange={e => setSelectedEmployee({ ...selectedEmployee, level: e.target.value })}
                      className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs text-gray-500">核心职责 (限20字)</span>
                  <input value={selectedEmployee.core_output} onChange={e => setSelectedEmployee({ ...selectedEmployee, core_output: e.target.value.slice(0, 20) })}
                    maxLength={20}
                    className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-xs text-gray-500">负荷</span>
                    <select value={selectedEmployee.workload} onChange={e => setSelectedEmployee({ ...selectedEmployee, workload: e.target.value as any })}
                      className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg">
                      <option value="low">低负荷</option>
                      <option value="medium">正常</option>
                      <option value="high">高负荷</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-500">类型</span>
                    <select value={selectedEmployee.employee_type} onChange={e => setSelectedEmployee({ ...selectedEmployee, employee_type: e.target.value as any })}
                      className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg">
                      <option value="full_time">正式</option>
                      <option value="intern">实习</option>
                      <option value="outsource">外包</option>
                      <option value="consultant">顾问</option>
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-xs text-gray-500">月薪成本 (K)</span>
                    <input type="number" step="1" value={selectedEmployee.monthly_cost || ''} onChange={e => setSelectedEmployee({ ...selectedEmployee, monthly_cost: parseFloat(e.target.value) || 0 })}
                      className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-500">入职日期</span>
                    <input type="date" value={selectedEmployee.hire_date || ''} onChange={e => setSelectedEmployee({ ...selectedEmployee, hire_date: e.target.value })}
                      className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 mt-1">
                    <input type="checkbox" checked={!!selectedEmployee.is_leader} onChange={e => setSelectedEmployee({ ...selectedEmployee, is_leader: e.target.checked ? 1 : 0 })}
                      className="rounded border-gray-300" />
                    组长/Leader
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-500">预计离职日期</span>
                    <input type="date" value={selectedEmployee.resign_date || ''} onChange={e => setSelectedEmployee({ ...selectedEmployee, resign_date: e.target.value })}
                      className="w-full mt-0.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </label>
                </div>
              </div>

              {/* Tags */}
              {selectedEmployee.tags && selectedEmployee.tags.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">能力标签</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {selectedEmployee.tags.map(t => (
                      <span key={t.id} className="text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: t.color + '20', color: t.color }}>
                        {t.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Work Content (近3月核心工作) */}
              {canEdit && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">近3月核心工作内容</span>
                    <button
                      onClick={() => {
                        const current = selectedEmployee.recent_work_content || [];
                        setSelectedEmployee({
                          ...selectedEmployee,
                          recent_work_content: [...current, { period: '', feishu_link: '', report_summary: '' }]
                        });
                      }}
                      className="text-[11px] text-primary-600 hover:text-primary-700 flex items-center gap-0.5"
                    >
                      <Plus size={12} />添加季度
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(selectedEmployee.recent_work_content || []).map((item, idx) => (
                      <div key={idx} className="p-2 rounded-lg bg-gray-50 border border-gray-100 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <input
                            value={item.period}
                            onChange={e => {
                              const arr = [...(selectedEmployee.recent_work_content || [])];
                              arr[idx] = { ...item, period: e.target.value };
                              setSelectedEmployee({ ...selectedEmployee, recent_work_content: arr });
                            }}
                            placeholder="2026Q1"
                            className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                          <button
                            onClick={() => {
                              const arr = (selectedEmployee.recent_work_content || []).filter((_, i) => i !== idx);
                              setSelectedEmployee({ ...selectedEmployee, recent_work_content: arr });
                            }}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X size={12} />
                          </button>
                        </div>
                        <input
                          value={item.feishu_link || ''}
                          onChange={e => {
                            const arr = [...(selectedEmployee.recent_work_content || [])];
                            arr[idx] = { ...item, feishu_link: e.target.value };
                            setSelectedEmployee({ ...selectedEmployee, recent_work_content: arr });
                          }}
                          placeholder="飞书链接 https://..."
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                        <textarea
                          value={item.report_summary || ''}
                          onChange={e => {
                            const arr = [...(selectedEmployee.recent_work_content || [])];
                            arr[idx] = { ...item, report_summary: e.target.value };
                            setSelectedEmployee({ ...selectedEmployee, recent_work_content: arr });
                          }}
                          placeholder="季度工作汇报摘要..."
                          rows={2}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
                        />
                      </div>
                    ))}
                    {(selectedEmployee.recent_work_content || []).length === 0 && (
                      <div className="text-[11px] text-gray-400 text-center py-2">暂无记录，点击添加</div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              {canEdit && (
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button onClick={handleUpdateEmployee}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg">
                    <Check size={14} />保存
                  </button>
                  <button onClick={handleDeleteEmployee}
                    className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ====== Modal: Change Password (Admin only) ====== */}
      {showChangePassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowChangePassword(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">修改访问密码</h3>
            <div className="space-y-3">
              <input
                type="password"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setChangePasswordError(''); setChangePasswordSuccess(false); }}
                placeholder="新密码（至少6位）"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setChangePasswordError(''); setChangePasswordSuccess(false); }}
                placeholder="确认新密码"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {changePasswordError && <p className="text-xs text-red-500">{changePasswordError}</p>}
              {changePasswordSuccess && <p className="text-xs text-emerald-600">密码修改成功！所有用户需使用新密码重新登录。</p>}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => {
                setShowChangePassword(false);
                setNewPassword('');
                setConfirmPassword('');
                setChangePasswordError('');
              }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
              <button onClick={async () => {
                if (newPassword.length < 6) {
                  setChangePasswordError('密码至少需要6位');
                  return;
                }
                if (newPassword !== confirmPassword) {
                  setChangePasswordError('两次输入的密码不一致');
                  return;
                }
                setIsChangingPassword(true);
                try {
                  await updateTalentHubPassword(newPassword);
                  // Clear authorization so everyone needs to re-login
                  localStorage.removeItem(TALENT_HUB_AUTHORIZED_KEY);
                  setChangePasswordSuccess(true);
                  setTimeout(() => {
                    setShowChangePassword(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setChangePasswordSuccess(false);
                    // Force re-login for current user too
                    setIsAuthorized(false);
                  }, 2000);
                } catch {
                  setChangePasswordError('密码修改失败');
                } finally {
                  setIsChangingPassword(false);
                }
              }} disabled={isChangingPassword} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">{isChangingPassword ? '修改中...' : '确认修改'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
